import { ConfigService } from './config.service.js';
import packageJson from '../../package.json' with { type: 'json' };
import { getCliName } from '../utils/cli-name.js';

// Colors for terminal output
const colors = {
    yellow: '\x1b[33m',
    cyan: '\x1b[36m',
    reset: '\x1b[0m',
    bold: '\x1b[1m',
};

// Cache to avoid checking too frequently
let lastCheckTime = 0;
let cachedLatestVersion: string | null = null;
let shouldCheckUpdates = true;
const CHECK_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

/**
 * Disable automatic update checks for this session
 */
export function disableUpdateCheck() {
    shouldCheckUpdates = false;
}

/**
 * Check if there's a newer version of jai1-client available
 * This runs silently and only shows a message if update is available
 */
export async function checkForClientUpdate(): Promise<void> {
    if (!shouldCheckUpdates) return;

    try {
        // Check only once per hour
        const now = Date.now();
        if (now - lastCheckTime < CHECK_INTERVAL_MS && cachedLatestVersion) {
            if (isNewerVersion(cachedLatestVersion, packageJson.version)) {
                showUpdateNotification(cachedLatestVersion);
            }
            return;
        }

        const configService = new ConfigService();
        const config = await configService.load();

        if (!config) {
            return; // Not initialized, skip check
        }

        // Fetch latest client version from API
        const response = await fetch(`${config.apiUrl}/api/versions/client`, {
            headers: {
                'JAI1-Access-Key': config.accessKey,
            },
            signal: AbortSignal.timeout(5000), // 5 second timeout
        });

        if (!response.ok) {
            return; // Silently fail
        }

        const data = await response.json() as {
            component: string;
            version: string;
            releasedAt: string;
            releaseNotes?: string;
            minClientVersion?: string;
        };
        lastCheckTime = now;
        cachedLatestVersion = data.version;

        if (isNewerVersion(data.version, packageJson.version)) {
            showUpdateNotification(data.version);
        }
    } catch {
        // Silently fail - don't interrupt user flow
    }
}

/**
 * Compare versions to check if remote is newer than local
 */
function isNewerVersion(remote: string, local: string): boolean {
    const remoteParts = remote.split('.').map(Number);
    const localParts = local.split('.').map(Number);

    for (let i = 0; i < 3; i++) {
        const r = remoteParts[i] || 0;
        const l = localParts[i] || 0;

        if (r > l) return true;
        if (r < l) return false;
    }

    return false;
}

/**
 * Show update notification to user
 */
function showUpdateNotification(latestVersion: string): void {
    console.log('');
    console.log(`${colors.yellow}╭──────────────────────────────────────────────────────────╮${colors.reset}`);
    console.log(`${colors.yellow}│${colors.reset}  ${colors.bold}⬆️  Update available!${colors.reset} ${packageJson.version} → ${colors.cyan}${latestVersion}${colors.reset}                  ${colors.yellow}│${colors.reset}`);
    console.log(`${colors.yellow}│${colors.reset}  Run: ${colors.cyan}${getCliName()} upgrade${colors.reset}                                   ${colors.yellow}│${colors.reset}`);
    console.log(`${colors.yellow}╰──────────────────────────────────────────────────────────╯${colors.reset}`);
    console.log('');
}

/**
 * Get current client version
 */
export function getClientVersion(): string {
    return packageJson.version;
}
